// Warehouse Reception (Phase 2) — gestionar tablet kiosk. Scan a label QR
// (ENG:DRUM:<tokenId> / ENG:PALLET:<code>) → the drum/pallet loads on the tablet
// → pick a depot + slot → put it away. Uses the browser-native BarcodeDetector
// (Android Chrome / Chromium kiosk) with a manual-entry fallback, so no extra
// npm dependency. Talks to eng-backend (:5000) warehouse API.
import { useState, useEffect, useRef, useCallback } from 'react';
import { drumSpecLabel } from '../../features/warehouse/warehouseUtils.js';
import './labelStation.css';
import { API } from '../../config/api';

const ENG_BASE = API.BACKEND;

function authHeaders() {
  return {
    'Content-Type': 'application/json',
    'x-auth-token': localStorage.getItem('token') || ''
  };
}
async function whReq(path, options = {}) {
  const res = await fetch(`${ENG_BASE}${path}`, { headers: authHeaders(), ...options });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || data?.success === false) {
    throw new Error(data?.error || data?.message || `HTTP ${res.status}`);
  }
  return data;
}

// ── camera QR scanner via the native BarcodeDetector API ──
function QrScanner({ onResult, onError }) {
  const videoRef = useRef(null);
  const rafRef = useRef(null);
  const streamRef = useRef(null);
  const [active, setActive] = useState(false);
  const supported = typeof window !== 'undefined' && 'BarcodeDetector' in window;

  const stop = useCallback(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    setActive(false);
  }, []);

  const start = useCallback(async () => {
    if (!supported) {
      onError?.('This device/browser has no QR camera support — type the code below.');
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' }
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      setActive(true);
      // eslint-disable-next-line no-undef
      const detector = new window.BarcodeDetector({ formats: ['qr_code'] });
      let last = 0;
      const tick = async () => {
        if (!streamRef.current) return;
        const now = Date.now();
        if (now - last > 350 && videoRef.current?.readyState === 4) {
          last = now;
          try {
            const codes = await detector.detect(videoRef.current);
            if (codes && codes.length) {
              const value = codes[0].rawValue;
              stop();
              onResult?.(value);
              return;
            }
          } catch {
            /* transient detect error — keep scanning */
          }
        }
        rafRef.current = requestAnimationFrame(tick);
      };
      rafRef.current = requestAnimationFrame(tick);
    } catch (e) {
      onError?.(`Camera error: ${e.message}`);
      stop();
    }
  }, [supported, onResult, onError, stop]);

  useEffect(() => () => stop(), [stop]);

  return (
    <div className="qr-scanner">
      <div className="qr-video-wrap">
        <video ref={videoRef} className="qr-video" muted playsInline />
        {active && <div className="qr-frame" />}
      </div>
      {!active ? (
        <button className="btn-print" onClick={start}>
          {supported ? 'START CAMERA' : 'CAMERA NOT SUPPORTED'}
        </button>
      ) : (
        <button className="btn-reprint" onClick={stop}>
          STOP CAMERA
        </button>
      )}
    </div>
  );
}

function ItemSummary({ item }) {
  const t = item?.traceability || {};
  const placed = item?.location?.kind === 'slot';
  return (
    <div className="production-info">
      <div className="info-row main">
        <span className="product-name">{item.cableName || t.productLabel || item.serial}</span>
        {t.color && (
          <span className="color-badge" data-color={String(t.color).toLowerCase()}>
            {t.color}
          </span>
        )}
        <span className={`machine-status ${placed ? 'stopped' : 'running'}`}>
          {item.type === 'europallet' ? 'PALLET' : 'DRUM'}
        </span>
        {item.customerName ? (
          <span className="rc-client">{item.customerName}</span>
        ) : (
          <span className="rc-stock">STOCK</span>
        )}
      </div>
      <div className="info-row details">
        <div className="detail">
          <span className="label">Serial</span>
          <span className="value job-code">{item.serial}</span>
        </div>
        <div className="detail">
          <span className="label">Length</span>
          <span className="value">{Number(item.lengthMeters || 0).toLocaleString()} m</span>
        </div>
        <div className="detail">
          <span className="label">Weight</span>
          <span className="value">{Number(item.weightKg || 0).toLocaleString()} kg</span>
        </div>
        {item.type === 'drum' && (drumSpecLabel(item) || item.drumName) && (
          <div className="detail">
            <span className="label">Drum</span>
            <span className="value" style={{ textTransform: 'capitalize' }}>
              {drumSpecLabel(item) || item.drumName}
            </span>
          </div>
        )}
        <div className="detail">
          <span className="label">Job</span>
          <span className="value job-code">{t.jobCode || t.jobId || '-'}</span>
        </div>
        <div className="detail">
          <span className="label">Current</span>
          <span className="value">{placed ? 'In a slot' : item.location?.kind || 'yard'}</span>
        </div>
      </div>
    </div>
  );
}

export default function WarehouseReception() {
  const [step, setStep] = useState('scan'); // 'scan' | 'place'
  const [item, setItem] = useState(null);
  const [buildings, setBuildings] = useState([]);
  const [buildingId, setBuildingId] = useState('');
  const [slots, setSlots] = useState([]);
  const [slotCode, setSlotCode] = useState('');
  const [manual, setManual] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [operator, setOperator] = useState('');
  const [pending, setPending] = useState([]);
  const [pendingLoading, setPendingLoading] = useState(false);

  const resolveScan = useCallback(async code => {
    if (!code) return;
    setBusy(true);
    setError(null);
    try {
      const res = await whReq('/api/v1/warehouse/reception/scan', {
        method: 'POST',
        body: JSON.stringify({ code })
      });
      if (res.multiple) {
        setError('That QR matched multiple items — scan the drum/pallet label instead.');
        return;
      }
      setItem(res.data.item);
      setSlotCode('');
      setStep('place');
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }, []);

  // load the manual-selection list whenever we're on the scan step
  const fetchPending = useCallback(async () => {
    setPendingLoading(true);
    try {
      const res = await whReq('/api/v1/warehouse/reception/pending');
      setPending(res?.data || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setPendingLoading(false);
    }
  }, []);

  useEffect(() => {
    if (step !== 'scan') return undefined;
    fetchPending();
    const interval = setInterval(fetchPending, 20000);
    return () => clearInterval(interval);
  }, [step, fetchPending]);

  // load depots when entering the place step
  useEffect(() => {
    if (step !== 'place') return;
    whReq('/api/v1/warehouse/buildings')
      .then(res => {
        const list = res?.data || [];
        setBuildings(list);
        if (list.length && !buildingId) setBuildingId(String(list[0]._id));
      })
      .catch(e => setError(e.message));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  // load free/suggested slots when item + building are known
  useEffect(() => {
    if (step !== 'place' || !item || !buildingId) return;
    let cancelled = false;
    (async () => {
      try {
        // prefer compatible suggestions; fall back to all free slots in the depot
        const sug = await whReq(
          `/api/v1/warehouse/items/${item.serial}/suggest-slots?building=${buildingId}`
        ).catch(() => null);
        let list = sug?.data || [];
        if (!list.length) {
          const free = await whReq(
            `/api/v1/warehouse/slots?building=${buildingId}&free=true`
          );
          list = free?.data || [];
        }
        if (!cancelled) setSlots(list);
      } catch (e) {
        if (!cancelled) setError(e.message);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [step, item, buildingId]);

  const handlePutAway = async () => {
    if (!item || !slotCode) {
      setFeedback({ type: 'error', msg: 'Pick a slot first' });
      setTimeout(() => setFeedback(null), 3000);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const inSlot = item.location?.kind === 'slot';
      const path = inSlot
        ? `/api/v1/warehouse/items/${item.serial}/move`
        : `/api/v1/warehouse/items/${item.serial}/put-away`;
      const body = inSlot ? { toSlotCode: slotCode } : { slotCode, operator };
      await whReq(path, { method: 'POST', body: JSON.stringify(body) });
      setFeedback({ type: 'success', msg: `Stored in ${slotCode}` });
      setTimeout(() => {
        setFeedback(null);
        setItem(null);
        setSlots([]);
        setSlotCode('');
        setManual('');
        setStep('scan');
      }, 1500);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const reset = () => {
    setItem(null);
    setSlots([]);
    setSlotCode('');
    setError(null);
    setStep('scan');
  };

  return (
    <div className="labelstation-root">
      <div className="app">
        <header className="app-header">
          <div className="header-left">
            <h1>Warehouse Reception</h1>
            <span className="machine-status running">
              {step === 'scan' ? 'SCAN' : 'PLACE'}
            </span>
          </div>
          <div className="operator-select">
            <input
              className="reception-operator"
              placeholder="Gestionar name (optional)"
              value={operator}
              onChange={e => setOperator(e.target.value)}
            />
          </div>
        </header>

        {error && <div className="error-bar">{error}</div>}

        <main className="app-main">
          {step === 'scan' && (
            <>
              <QrScanner onResult={resolveScan} onError={setError} />
              <div className="reception-manual">
                <input
                  className="reception-input"
                  placeholder="…or type/scan the code (ENG:DRUM:K-… / ENG:PALLET:PP-…)"
                  value={manual}
                  onChange={e => setManual(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && resolveScan(manual.trim())}
                />
                <button
                  className="btn-print"
                  disabled={busy || !manual.trim()}
                  onClick={() => resolveScan(manual.trim())}
                >
                  {busy ? 'LOOKING…' : 'LOOK UP'}
                </button>
              </div>

              {/* manual-selection table — fallback when QR scanning isn't available */}
              <div className="reception-list">
                <div className="reception-list-head">
                  <span className="queue-title">
                    Awaiting reception {pending.length ? `(${pending.length})` : ''}
                  </span>
                  <button className="btn-reprint reception-refresh" onClick={fetchPending}>
                    {pendingLoading ? '…' : 'REFRESH'}
                  </button>
                </div>
                <div className="recv-table-wrap">
                  <table className="recv-table">
                    <thead>
                      <tr>
                        <th>Product</th>
                        <th>Type</th>
                        <th>For</th>
                        <th className="num">Length</th>
                        <th className="num">Weight</th>
                        <th>Job</th>
                        <th>Where</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {pending.map(r => (
                        <tr key={r.code}>
                          <td>
                            <span className="rc-name">{r.cableName}</span>
                            {r.color && (
                              <span
                                className="qi-color"
                                data-color={String(r.color).toLowerCase()}
                              >
                                {r.color}
                              </span>
                            )}
                          </td>
                          <td>
                            <span className={`rc-type ${r.type}`}>
                              {r.type === 'europallet' ? 'PALLET' : 'DRUM'}
                            </span>
                            {r.coilCount ? (
                              <span className="rc-sub"> {r.coilCount} coils</span>
                            ) : null}
                            {(drumSpecLabel(r) || r.drumName) && (
                              <span className="rc-sub" style={{ textTransform: 'capitalize' }}>
                                {' '}{drumSpecLabel(r) || r.drumName}
                              </span>
                            )}
                          </td>
                          <td>
                            {r.customerName ? (
                              <span className="rc-client">{r.customerName}</span>
                            ) : (
                              <span className="rc-stock">STOCK</span>
                            )}
                          </td>
                          <td className="num">{Number(r.lengthMeters || 0).toLocaleString()} m</td>
                          <td className="num">
                            {r.weightKg ? `${Number(r.weightKg).toLocaleString()} kg` : '—'}
                          </td>
                          <td className="rc-job">{r.job || '—'}</td>
                          <td>
                            <span className={`rc-where ${r.status === 'new' ? 'new' : ''}`}>
                              {r.status === 'new' ? 'production' : r.locationKind || '-'}
                            </span>
                          </td>
                          <td>
                            <button
                              className="rc-select"
                              disabled={busy}
                              onClick={() => resolveScan(r.code)}
                            >
                              SELECT
                            </button>
                          </td>
                        </tr>
                      ))}
                      {!pending.length && !pendingLoading && (
                        <tr>
                          <td colSpan={8} className="recv-empty">
                            Nothing awaiting reception
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}

          {step === 'place' && item && (
            <>
              <ItemSummary item={item} />

              <div className="reception-pick">
                <label className="pick-label">Depot</label>
                <select
                  className="reception-select"
                  value={buildingId}
                  onChange={e => {
                    setBuildingId(e.target.value);
                    setSlotCode('');
                  }}
                >
                  {buildings.map(b => (
                    <option key={b._id} value={b._id}>
                      {b.name} ({b.code})
                    </option>
                  ))}
                </select>

                <label className="pick-label">
                  Free slots {slots.length ? `(${slots.length})` : ''}
                </label>
                <div className="slot-grid">
                  {slots.map(s => (
                    <button
                      key={s.code}
                      className={`slot-chip ${slotCode === s.code ? 'sel' : ''}`}
                      onClick={() => setSlotCode(s.code)}
                    >
                      {s.code}
                    </button>
                  ))}
                  {!slots.length && (
                    <span className="queue-empty">No free slots in this depot</span>
                  )}
                </div>

                <input
                  className="reception-input"
                  placeholder="…or scan/type a slot code (e.g. B1-A-12-L3)"
                  value={slotCode}
                  onChange={e => setSlotCode(e.target.value)}
                />
              </div>

              <div className="print-buttons">
                <button className="btn-print" disabled={busy || !slotCode} onClick={handlePutAway}>
                  {busy ? 'STORING…' : `STORE IN ${slotCode || '…'}`}
                </button>
                <button className="btn-reprint" onClick={reset}>
                  CANCEL
                </button>
              </div>
              {feedback && <div className={`feedback ${feedback.type}`}>{feedback.msg}</div>}
            </>
          )}
        </main>
      </div>
    </div>
  );
}
